import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AssetReportHeader } from '../../components/ValuationReport/ReportHeader/header.module';
import { AssetProfile } from '../../components/ValuationReport/ReportProfile/assetProfile.module';
import { Valuation } from '../../components/ValuationReport/ReportValuation/valuation.module';
import { GlobalStyle, PageModulesContainer } from './assetReport.styles';
import { FinancialData } from '../../components/ValuationReport/ReportFinancial/financial.module';
import { Zone } from '../../components/ValuationReport/ReportZone/zone.module';
import { Comparables } from '../../components/ValuationReport/ReportComparables/comparables.module';
import { Glossary } from '../../components/ValuationReport/ReportGlossary/glossary.module';
import { Terms } from '../../components/ValuationReport/ReportTerms/terms.module';
import { OfferDemand } from '../../components/ValuationReport/ReportOfferDemand/offerDemand.component';
import { ZonePopulation } from '../../components/ValuationReport/ReportZonePopulation/zonePopulation.component';
import { AssetReportFooter } from '../../components/ValuationReport/ReportFooter/assetReportFooter.component';
import { ReportCover } from '../../components/ValuationReport/ReportCover/cover.component';
import { AgentInfo } from '../../components/ValuationReport/ReportAgentInfo/agentInfo.component';
import { ReportBackCover } from '../../components/ValuationReport/ReportCover/backCover.component';
import { ModuleToggle } from '../../components/ValuationReport/ModuleToggle/moduleToggle.component';
import { defaultModuleVisibility } from '../../components/ValuationReport/ModuleToggle/moduleConfig';
import I18nProvider from '../../i18n/i18n';
import { useAssetReportStore } from '../../zustand/assetReport.store';
import {
  getComparablesECO,
  getFinancialIndicators,
  getHistogramIndicators,
  getInitialIndicators,
  getOfferDemandIndicators,
  getSelectedStyle,
  getSimilarComparables,
  getUserData,
  getUserReportData,
  getValuationData,
  getValuationIndicators,
  getZoneIndicators,
  getZonePopulationIndicators,
  sendErrorReport,
} from '../../api/api';
import {
  getDatesRange,
  getDistance,
  getPeriodCodes,
  mountAssetProfileData,
  mountComparablesData,
  mountFinancialData,
  mountHistogramData,
  mountOfferDemandData,
  mountValuationData,
  mountZoneData,
  mountZonePopulationData,
  resolveEstimatedPriceSqm,
} from '../../helpers/utils';
import { getCorrectPrice } from '../../helpers/priceHelpers';
import { PreviewPage } from '../../styles/global.styles';
import { glossaryTexts, termsTexts } from '../../helpers/dictionaries.helper';
import { AssetDetails, AssetFeatures } from '../../types/interfaces';
import { AssetProfileDataModel } from '../../types/assetProfile.interface';
import { ReportDataResponse } from '../../types/reportData.interface';
import {
  BoundaryResponse,
  FinancialDataModel,
  HistogramDataModel,
  HistogramIndicatorsBundle,
  OfferDemandDataModel,
  ScatterDataModel,
  ZoneDataModel,
  ZonePopulationDataModel,
} from '../../types/indicators.interfaces';
import {
  ValuationDataModel,
  ValuationFeatures,
  ValuationRequestParams,
} from '../../types/valuation.interface';
import {
  Asset,
  ComparablesDataModel,
} from '../../types/comparables.interfaces';
import ErrorBoundary from '../../config/errors.config';
import { useErrorStore } from '../../zustand/errors.store';
import { LoadingComponent } from '../../components/ValuationReport/ReportLoading/loading.component';
import { ErrorComponent } from '../../components/ValuationReport/ReportError/errorModule.component';
import { useApiStore } from '../../zustand/api.store';
import { assetSchema } from '../../helpers/validations/assetReport.validations';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { getRequestErrorLog } from '../../helpers/internUtils';
import { AssetValueOnMarket } from '../../components/ValuationReport/ReportValueOnMarketGraph/assetValueOnMarket.component';
import { AssetComparativeGraph } from '../../components/ValuationReport/ReportComparativeGraph/comparativeGraph.component';
import { toNumber } from 'lodash';
import { NoDataHalfPage } from '../../components/ValuationReport/NoDataComponent/noData.component';
import DownloadButton from '../../components/ValuationReport/DownloadButton/downloadButton.component';
import { handlePDFGeneration } from '../../helpers/pdfDownload';
// Tipo para la respuesta de valuación
interface ValuationResponse {
  asset: {
    nbaths: number;
    rooms: number;
    floor: number;
    has_elevator: boolean;
    has_terrace: boolean;
    has_storage: boolean;
    has_garage: boolean;
    has_air_conditioner: boolean;
    has_pool: boolean;
    is_exterior: boolean;
  };
  ECO: {
    competitors_id: string[];
  };
  best_value: number;
  best_value_pm2: number;
  no_data: boolean;
}

export function AssetReport() {
  const intl = useIntl();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { saveToken, userToken } = useAssetReportStore((state) => state);

  // Get country parameter from URL
  const country = searchParams.get('country') || 'es';

  // Function to get initial module visibility based on country
  const getInitialModuleVisibility = (
    countryCode: string
  ): Record<string, boolean> => {
    const initialVisibility = { ...defaultModuleVisibility };

    // For Colombia (co), Mexico (mx) and Peru (pe), disable specific modules by default
    if (countryCode === 'co' || countryCode === 'mx' || countryCode === 'pe') {
      initialVisibility.marketValueGraph = false;
      initialVisibility.financialData = false;
      initialVisibility.offerDemand = false;
      initialVisibility.zonePopulation = false;
      initialVisibility.zone = false;
    }

    return initialVisibility;
  };

  const [reportData, setReportData] = useState<ReportDataResponse>();
  const [initialIndicators, setInitialIndicators] =
    useState<BoundaryResponse>();
  const [initialAssetDetails, setInitialAssetDetails] =
    useState<AssetDetails>();
  const [initialAssetFeatures, setInitialAssetFeatures] =
    useState<Partial<AssetFeatures>>();
  const [assetValue, setAssetValue] = useState<number | null>();
  const [selectedComparables, setSelectedComparables] = useState<String[]>();
  const [assetValueSqm, setAssetValueSqm] = useState<number | null>();
  const [selectedLanguage, setSelectedLanguage] = useState<string>();
  const [selectedPriceType, setSelectedPriceType] = useState<
    'original' | 'closing'
  >('original');

  const [assetProfileData, setAssetProfileData] =
    useState<AssetProfileDataModel>();
  const [saleValuationData, setSaleValuationData] =
    useState<ValuationResponse>();
  const [rentValuationData, setRentValuationData] =
    useState<ValuationResponse>();
  const [valuationData, setValuationData] = useState<ValuationDataModel>();
  const [valuationIndicators, setValuationIndicators] = useState();
  const [histogramIndicators, setHistogramIndicators] =
    useState<HistogramIndicatorsBundle[]>();
  const [histogramData, setHistogramData] = useState<HistogramDataModel>();
  const [scatterData, setScatterData] = useState<ScatterDataModel>();
  const [ECOComparablesList, setECOComparablesList] = useState<String[]>();
  const [comparablesData, setComparablesData] =
    useState<ComparablesDataModel>();
  const [financialData, setFinancialData] = useState<FinancialDataModel>();
  const [offerDemandData, setOfferDemandData] =
    useState<OfferDemandDataModel | null>();
  const [zonePopulationData, setZonePopulationData] =
    useState<ZonePopulationDataModel | null>();
  const [zoneData, setZoneData] = useState<ZoneDataModel | null>();
  const [estimatedValueMin, setEstimatedValueMin] = useState<number | null>();
  const [estimatedValueMax, setEstimatedValueMax] = useState<number | null>();
  const [valuationFeatures, setValuationFeatures] =
    useState<ValuationFeatures>();

  // Estado para controlar la visibilidad de los módulos
  const [visibleModules, setVisibleModules] = useState<Record<string, boolean>>(
    () => getInitialModuleVisibility(country)
  );

  // render states
  const { errorReport, addError } = useErrorStore((state) => state);
  const {
    errorReportSent,
    setErrorReportSent,
    readyToSendErrorReport,
    similarComparablesCall,
    markApiCallsAsCompleted,
  } = useApiStore((state) => state);
  const [errorsFound, setErrorsFound] = useState(false);
  const [valuationFailed, setValuationFailed] = useState(false);
  const [initialCallsCompleted, setInitialCallsCompleted] = useState(false);
  const [similarComparablesFetched, setSimilarComparablesFetched] =
    useState(false);
  const [renderReport, setRenderReport] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  // La exclusión automática de listing_type 8/12 se aplica una sola vez, para que
  // después se respete lo que el usuario configure en el panel de módulos
  const autoExcludeApplied = useRef(false);

  // Custom reports (report_type 2/3) bring their own valuation values in the URL
  // params, so the AVM is optional for them (e.g. warehouses/land with no AVM
  // coverage): a valuation without data must not block the report
  const hasCustomValuationValues = Boolean(
    initialAssetDetails &&
      (initialAssetDetails.reportType === 2 ||
        initialAssetDetails.reportType === 3) &&
      initialAssetDetails.estimatedPrice &&
      initialAssetDetails.estimatedPriceSqm
  );

  // Log assetValue changes - removed empty useEffect to prevent infinite loops
  // useEffect(() => {}, [assetValue]);

  // Log assetValueSqm changes - removed empty useEffect to prevent infinite loops
  // useEffect(() => {}, [assetValueSqm]);

  // fetch the user token
  useEffect(() => {
    const key = searchParams.get('key');
    if (key) {
      saveToken(key);
    }
  }, [location.search]); // Use location.search instead of searchParams to avoid recreation on every render
  // get the url params and fill features and details
  useEffect(() => {
    // Get the searchParams from the URL
    const searchParams = new URLSearchParams(window.location.search);

    // Define the asset details structure
    const data: AssetDetails = {
      nbaths:
        searchParams.has('n_baths') &&
        !isNaN(Number(searchParams.get('n_baths')))
          ? toNumber(searchParams.get('n_baths'))
          : null,
      rooms:
        searchParams.has('n_rooms') &&
        !isNaN(Number(searchParams.get('n_rooms')))
          ? toNumber(searchParams.get('n_rooms'))
          : null,
      listingType: Number(searchParams.get('listing_type')),
      operation: Number(searchParams.get('operation')),
      lat: Number(searchParams.get('lat')),
      lon: Number(searchParams.get('lon')),
      address: searchParams.get('address') ?? null,
      area: Number(searchParams.get('area')),
      parcelArea:
        searchParams.has('parcel_area') &&
        !isNaN(Number(searchParams.get('parcel_area'))) &&
        Number(searchParams.get('parcel_area')) !== 0
          ? toNumber(searchParams.get('parcel_area'))
          : null,
      floor:
        searchParams.has('floor') && !isNaN(Number(searchParams.get('floor')))
          ? toNumber(searchParams.get('floor'))
          : null,
      cadastralRef: searchParams.get('cadastral_ref') ?? null,
      propertyType:
        searchParams.has('property_type') &&
        !isNaN(Number(searchParams.get('property_type')))
          ? toNumber(searchParams.get('property_type'))
          : null,
      yearBuilt:
        searchParams.has('year_build') &&
        !isNaN(Number(searchParams.get('year_build')))
          ? toNumber(searchParams.get('year_build'))
          : null,
      reportType: Number(searchParams.get('report_type')),
      pulseType: Number(searchParams.get('pulse_type')),
      buildStatus:
        searchParams.has('build_status') &&
        !isNaN(Number(searchParams.get('build_status')))
          ? toNumber(searchParams.get('build_status'))
          : null,
      estimatedPrice:
        searchParams.has('estimated_price') &&
        Number(searchParams.get('estimated_price'))
          ? toNumber(searchParams.get('estimated_price'))
          : null,
      estimatedPriceSqm:
        searchParams.has('estimated_price_sqm') &&
        Number(searchParams.get('estimated_price_sqm'))
          ? toNumber(searchParams.get('estimated_price_sqm'))
          : null,
      yield:
        searchParams.has('yield') && !isNaN(Number(searchParams.get('yield')))
          ? toNumber(searchParams.get('yield'))
          : undefined,
      rentValue:
        searchParams.has('rent_value') &&
        !isNaN(Number(searchParams.get('rent_value')))
          ? toNumber(searchParams.get('rent_value'))
          : undefined,
      saleValue:
        searchParams.has('sale_value') &&
        !isNaN(Number(searchParams.get('sale_value')))
          ? toNumber(searchParams.get('sale_value'))
          : undefined,
      rentValueSqm:
        searchParams.has('rent_value_sqm') &&
        !isNaN(Number(searchParams.get('rent_value_sqm')))
          ? toNumber(searchParams.get('rent_value_sqm'))
          : undefined,
      saleValueSqm:
        searchParams.has('sale_value_sqm') &&
        !isNaN(Number(searchParams.get('sale_value_sqm')))
          ? toNumber(searchParams.get('sale_value_sqm'))
          : undefined,
    };

    // estimated_price_sqm no siempre llega en la URL: se deriva del precio y la superficie
    data.estimatedPriceSqm =
      resolveEstimatedPriceSqm(
        data.estimatedPrice,
        data.estimatedPriceSqm,
        data.area
      ) ?? undefined;

    // Set values if estimated_price and estimated_price_sqm are provided
    if (
      data.estimatedPrice &&
      data.estimatedPriceSqm &&
      (data.reportType == 2 || data.reportType == 3)
    ) {
      setAssetValue(data.estimatedPrice);
      setAssetValueSqm(data.estimatedPriceSqm);
    } else if (
      (data.reportType == 2 || data.reportType == 3) &&
      (!data.estimatedPrice || !data.estimatedPriceSqm)
    ) {
      const errorLog = getRequestErrorLog(
        new Error(
          'estimated_price and estimated_price_sqm required in custom report type.'
        ),
        `${window.location.href}`,
        {}
      );
      addError(errorLog);
      setErrorsFound(true);
    }

    // Set tab title
    document.title =
      data.operation === 1
        ? intl.formatMessage({ id: 'saleReport' })
        : intl.formatMessage({ id: 'rentReport' });

    // Error handling for asset details
    try {
      assetSchema.validateSync(data);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorLog = getRequestErrorLog(
          error,
          `${window.location.href}`,
          data
        );
        addError(errorLog);
        setErrorsFound(true);
        return console.error('Validation error:', error.errors);
      } else {
        setErrorsFound(true);
        const errorLog = getRequestErrorLog(
          error,
          `${window.location.href}`,
          data
        );
        addError(errorLog);
        setErrorsFound(true);
        return console.error('Unexpected error:', error);
      }
    }

    // Define the asset features structure
    const assetFeatures: AssetFeatures = {
      has_air_conditioner: searchParams.has('has_air_conditioner')
        ? Math.round(parseFloat(searchParams.get('has_air_conditioner') || '0'))
        : null,
      has_garage: searchParams.has('has_garage')
        ? Math.round(parseFloat(searchParams.get('has_garage') || '0'))
        : null,
      has_elevator: searchParams.has('has_elevator')
        ? Math.round(parseFloat(searchParams.get('has_elevator') || '0'))
        : null,
      has_storage: searchParams.has('has_storage')
        ? Math.round(parseFloat(searchParams.get('has_storage') || '0'))
        : null,
      has_pool: searchParams.has('has_pool')
        ? Math.round(parseFloat(searchParams.get('has_pool') || '0'))
        : null,
      has_terrace: searchParams.has('has_terrace')
        ? Math.round(parseFloat(searchParams.get('has_terrace') || '0'))
        : null,
      is_exterior: searchParams.has('is_exterior')
        ? Math.round(parseFloat(searchParams.get('is_exterior') || '0'))
        : null,
      has_automatic_door: searchParams.has('has_automatic_door')
        ? Math.round(parseFloat(searchParams.get('has_automatic_door') || '0'))
        : null,
      has_people: searchParams.has('has_people')
        ? Math.round(parseFloat(searchParams.get('has_people') || '0'))
        : null,
      has_security: searchParams.has('has_security')
        ? Math.round(parseFloat(searchParams.get('has_security') || '0'))
        : null,
      has_pavement: searchParams.has('has_pavement')
        ? Math.round(parseFloat(searchParams.get('has_pavement') || '0'))
        : null,
      has_water: searchParams.has('has_water')
        ? Math.round(parseFloat(searchParams.get('has_water') || '0'))
        : null,
      has_street_lighting: searchParams.has('has_street_lighting')
        ? Math.round(parseFloat(searchParams.get('has_street_lighting') || '0'))
        : null,
      has_electricity: searchParams.has('has_electricity')
        ? Math.round(parseFloat(searchParams.get('has_electricity') || '0'))
        : null,
      has_natural_gas: searchParams.has('has_natural_gas')
        ? Math.round(parseFloat(searchParams.get('has_natural_gas') || '0'))
        : null,
      has_sewerage: searchParams.has('has_sewerage')
        ? Math.round(parseFloat(searchParams.get('has_sewerage') || '0'))
        : null,
    };

    // Filter out null values from the features
    const filteredFeatures: Partial<AssetFeatures> = Object.fromEntries(
      Object.entries(assetFeatures).filter(
        ([_, value]) => value !== null && value !== undefined
      )
    );

    const filteredDetails: AssetDetails = {
      listingType: data.listingType,
      operation: data.operation,
      lat: data.lat,
      lon: data.lon,
      area: data.area,
      reportType: data.reportType,
      pulseType: data.pulseType,
      address: data.address ?? '',
      ...Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value != null)
      ),
    };

    // Update the initial state with filtered features
    setInitialAssetFeatures(filteredFeatures);

    // Get the selected comparables and split into array
    const selectedComparables = searchParams.get('selected_comparables');
    if (selectedComparables) {
      const comparablesArray = selectedComparables.split(',');
      setSelectedComparables(comparablesArray);
    }

    const priceType = searchParams.get('price_type');
    if (priceType === 'closing') {
      setSelectedPriceType('closing');
    }

    // Set the initial asset details
    setInitialAssetDetails(filteredDetails);
  }, []); // Removed dependencies as they are stable functions from stores/hooks

  // user data calls
  useEffect(() => {
    if (userToken && !initialCallsCompleted && initialAssetDetails) {
      setInitialCallsCompleted(true);
      const fetchData = async () => {
        try {
          const [userData, selectedStyle] = await Promise.all([
            getUserData(),
            getSelectedStyle(),
          ]);

          if (userData !== null) {
            setSelectedLanguage(userData.language);
          }

          if (selectedStyle) {
            const userReportData = await getUserReportData(selectedStyle);
            if (userReportData) {
              setReportData({
                ...userReportData,
                ...reportData,
                reportType: initialAssetDetails.reportType,
                pulseType: initialAssetDetails.pulseType,
              });
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchData();
    }
  }, [userToken, initialAssetDetails, initialCallsCompleted]); // Include reportId in the dependency array to trigger fetchData when it changes

  // Initial setup and valuation call
  useEffect(() => {
    if (initialAssetDetails && !initialIndicators && initialAssetFeatures) {
      const initialize = async () => {
        if (initialAssetDetails) {
          const [initialIndicatorsResponse] = await Promise.all([
            initIndicatorsCall(),
          ]);

          setInitialIndicators(initialIndicatorsResponse);

          // Skip valuation calls when report_type=2 or 3 and listing_type=8 or listing_type=12
          const shouldSkipValuationCalls =
            (initialAssetDetails.reportType === 2 ||
              initialAssetDetails.reportType === 3) &&
            (initialAssetDetails.listingType === 8 ||
              initialAssetDetails.listingType === 12);
          if (shouldSkipValuationCalls) {
            // Set default values and mark as completed without making valuation calls
            setValuationFeatures({
              target_has_elevator: Boolean(
                initialAssetFeatures.has_elevator || 0
              ),
              target_has_terrace: Boolean(
                initialAssetFeatures.has_terrace || 0
              ),
              target_has_storage: Boolean(
                initialAssetFeatures.has_storage || 0
              ),
              target_has_garage: Boolean(initialAssetFeatures.has_garage || 0),
              target_has_air_conditioner: Boolean(
                initialAssetFeatures.has_air_conditioner || 0
              ),
              target_has_pool: Boolean(initialAssetFeatures.has_pool || 0),
              target_is_exterior: Boolean(
                initialAssetFeatures.is_exterior || 0
              ),
            });

            // Set empty ECO comparables list
            setECOComparablesList([]);

            // Set empty valuation data
            setSaleValuationData(undefined);
            setRentValuationData(undefined);

            // Create default valuation data structure for custom reports
            const defaultValuationData: ValuationResponse = {
              asset: {
                nbaths: initialAssetDetails.nbaths || 0,
                rooms: initialAssetDetails.rooms || 0,
                floor: initialAssetDetails.floor || 0,
                has_elevator: Boolean(initialAssetFeatures.has_elevator || 0),
                has_terrace: Boolean(initialAssetFeatures.has_terrace || 0),
                has_storage: Boolean(initialAssetFeatures.has_storage || 0),
                has_garage: Boolean(initialAssetFeatures.has_garage || 0),
                has_air_conditioner: Boolean(
                  initialAssetFeatures.has_air_conditioner || 0
                ),
                has_pool: Boolean(initialAssetFeatures.has_pool || 0),
                is_exterior: Boolean(initialAssetFeatures.is_exterior || 0),
              },
              ECO: {
                competitors_id: [],
              },
              best_value: initialAssetDetails.estimatedPrice || 0,
              best_value_pm2: initialAssetDetails.estimatedPriceSqm || 0,
              no_data: false,
            };

            // Set default valuation data for both sale and rent
            setSaleValuationData(defaultValuationData);
            setRentValuationData(defaultValuationData);

            // Mark as completed since we're not making valuation calls
            markApiCallsAsCompleted(5);
            markApiCallsAsCompleted(8);
            setSimilarComparablesFetched(true);

            // Set asset values from URL parameters for custom report
            setAssetValue(initialAssetDetails.estimatedPrice);
            setAssetValueSqm(initialAssetDetails.estimatedPriceSqm);
          } else {
            // Original logic for making valuation calls
            const [rentValuationResponse, saleValuationResponse] =
              await Promise.all([rentValuationCall(), saleValuationCall()]);

            // Check if the valuation call for the current operation failed or returned 204
            const operationValuationResponse =
              initialAssetDetails.operation === 1
                ? saleValuationResponse
                : rentValuationResponse;

            // Check if both calls returned 204 (no_data) - not failed, just no data
            const bothReturned204 =
              rentValuationResponse?.no_data && saleValuationResponse?.no_data;

            // Check if the operation's valuation call returned 204 (no_data)
            const operationReturned204 =
              operationValuationResponse?.no_data === true;

            // Check if both calls failed (null/undefined)
            const bothFailed =
              (rentValuationResponse === null ||
                rentValuationResponse === undefined) &&
              (saleValuationResponse === null ||
                saleValuationResponse === undefined);

            // Check if the operation's valuation call failed (null/undefined)
            const operationFailed =
              operationValuationResponse === null ||
              operationValuationResponse === undefined;

            // If both calls returned 204 or the operation's call returned 204, show valuation error message
            // Custom reports keep going: their value comes from the URL params, not from the AVM
            if (
              (bothReturned204 || operationReturned204) &&
              !hasCustomValuationValues
            ) {
              console.error(
                '❌ Valuation calls returned 204 (no_data) - showing valuation failed error component'
              );
              setValuationFailed(true);
              setErrorsFound(true);
              return;
            }

            // If both calls failed or the operation's call failed, show general error message
            if ((bothFailed || operationFailed) && !hasCustomValuationValues) {
              console.error(
                '❌ Valuation calls failed (null/undefined) - showing general error component'
              );
              setErrorsFound(true);
              return;
            }

            if (
              rentValuationResponse &&
              saleValuationResponse &&
              !saleValuationResponse.no_data &&
              !rentValuationResponse.no_data
            ) {
              const selectedOperationValuation =
                initialAssetDetails.operation === 1
                  ? saleValuationResponse
                  : rentValuationResponse;

              // Update initialAssetDetails with data from valuation if not provided in URL
              setInitialAssetDetails((prev) => ({
                ...prev!,
                nbaths: prev?.nbaths ?? selectedOperationValuation.asset.nbaths,
                rooms: prev?.rooms ?? selectedOperationValuation.asset.rooms,
                floor: prev?.floor ?? selectedOperationValuation.asset.floor,
              }));

              setValuationFeatures({
                target_has_elevator:
                  selectedOperationValuation.asset?.has_elevator,
                target_has_terrace:
                  selectedOperationValuation.asset?.has_terrace,
                target_has_storage:
                  selectedOperationValuation.asset?.has_storage,
                target_has_garage: selectedOperationValuation.asset?.has_garage,
                target_has_air_conditioner:
                  selectedOperationValuation.asset?.has_air_conditioner,
                target_has_pool: selectedOperationValuation.asset?.has_pool,
                target_is_exterior:
                  selectedOperationValuation.asset?.is_exterior,
              });

              setECOComparablesList(
                selectedOperationValuation?.ECO?.competitors_id
              );
              setSaleValuationData(saleValuationResponse);
              setRentValuationData(rentValuationResponse);

              if (
                selectedOperationValuation?.ECO?.competitors_id?.length === 0 &&
                !selectedComparables
              ) {
                markApiCallsAsCompleted(5);
                markApiCallsAsCompleted(8);
                setSimilarComparablesFetched(true);
              }

              if (
                (initialAssetDetails.reportType == 1 ||
                  initialAssetDetails.reportType == 3) &&
                initialAssetDetails.listingType !== 8 &&
                initialAssetDetails.listingType !== 12
              ) {
                setAssetValue(selectedOperationValuation.best_value);
                setAssetValueSqm(selectedOperationValuation.best_value_pm2);
              } else if (
                initialAssetDetails.reportType == 2 ||
                initialAssetDetails.reportType == 3
              ) {
                setAssetValue(initialAssetDetails.estimatedPrice);
                setAssetValueSqm(initialAssetDetails.estimatedPriceSqm);
              }
            } else {
              // Handle cases where at least one valuation response has no_data (204)
              // or when responses are null/undefined
              console.log(
                '📊 Valuation response has no data (204 status) or is null - using estimated values'
              );

              // Determine which valuation response corresponds to the current operation
              const operationValuationResponse =
                initialAssetDetails.operation === 1
                  ? saleValuationResponse
                  : rentValuationResponse;

              // Set undefined for responses with no data or null (failed)
              if (saleValuationResponse && saleValuationResponse.no_data) {
                setSaleValuationData(undefined);
              } else if (saleValuationResponse) {
                setSaleValuationData(saleValuationResponse);
              } else if (saleValuationResponse === null) {
                // Explicitly set to undefined when call failed
                setSaleValuationData(undefined);
              }

              if (rentValuationResponse && rentValuationResponse.no_data) {
                setRentValuationData(undefined);
              } else if (rentValuationResponse) {
                setRentValuationData(rentValuationResponse);
              } else if (rentValuationResponse === null) {
                // Explicitly set to undefined when call failed
                setRentValuationData(undefined);
              }

              // Set ECO comparables list based on the operation's valuation response
              // Use comparables from the operation's call, or empty list if no data/empty
              if (
                operationValuationResponse &&
                !operationValuationResponse.no_data &&
                operationValuationResponse.ECO?.competitors_id &&
                operationValuationResponse.ECO.competitors_id.length > 0
              ) {
                // Use ECO comparables from the operation's valuation response
                setECOComparablesList(
                  operationValuationResponse.ECO.competitors_id
                );
              } else {
                // Set empty list if:
                // - The operation's call has no_data (204)
                // - The operation's call has no ECO comparables or empty array
                // - The operation's response is null/undefined
                setECOComparablesList([]);
              }

              // Set asset values based on available data
              // Priority: 1) Operation's valuation response, 2) Other valuation response, 3) Estimated values
              if (
                operationValuationResponse &&
                !operationValuationResponse.no_data &&
                operationValuationResponse.best_value &&
                operationValuationResponse.best_value_pm2
              ) {
                // Use values from the operation's valuation response
                setAssetValue(operationValuationResponse.best_value);
                setAssetValueSqm(operationValuationResponse.best_value_pm2);
                console.log('💰 Using values from operation valuation:', {
                  price: operationValuationResponse.best_value,
                  priceSqm: operationValuationResponse.best_value_pm2,
                });
              } else if (
                saleValuationResponse &&
                !saleValuationResponse.no_data &&
                saleValuationResponse.best_value &&
                saleValuationResponse.best_value_pm2
              ) {
                // Fallback to sale valuation if operation's response has no data
                setAssetValue(saleValuationResponse.best_value);
                setAssetValueSqm(saleValuationResponse.best_value_pm2);
                console.log('💰 Using values from sale valuation (fallback):', {
                  price: saleValuationResponse.best_value,
                  priceSqm: saleValuationResponse.best_value_pm2,
                });
              } else if (
                rentValuationResponse &&
                !rentValuationResponse.no_data &&
                rentValuationResponse.best_value &&
                rentValuationResponse.best_value_pm2
              ) {
                // Fallback to rent valuation if operation's response has no data
                setAssetValue(rentValuationResponse.best_value);
                setAssetValueSqm(rentValuationResponse.best_value_pm2);
                console.log('💰 Using values from rent valuation (fallback):', {
                  price: rentValuationResponse.best_value,
                  priceSqm: rentValuationResponse.best_value_pm2,
                });
              } else if (
                initialAssetDetails.estimatedPrice &&
                initialAssetDetails.estimatedPriceSqm
              ) {
                // Last resort: use estimated values if available
                setAssetValue(initialAssetDetails.estimatedPrice);
                setAssetValueSqm(initialAssetDetails.estimatedPriceSqm);
                console.log('💰 Using estimated values (last resort):', {
                  price: initialAssetDetails.estimatedPrice,
                  priceSqm: initialAssetDetails.estimatedPriceSqm,
                });
              } else {
                console.warn(
                  '⚠️ No asset values available for 204 response - keeping existing values or using 0'
                );
                // Don't set assetValue/assetValueSqm to avoid overwriting existing values
              }

              // Set default valuation features from initial asset features
              setValuationFeatures({
                target_has_elevator: Boolean(
                  initialAssetFeatures.has_elevator || 0
                ),
                target_has_terrace: Boolean(
                  initialAssetFeatures.has_terrace || 0
                ),
                target_has_storage: Boolean(
                  initialAssetFeatures.has_storage || 0
                ),
                target_has_garage: Boolean(
                  initialAssetFeatures.has_garage || 0
                ),
                target_has_air_conditioner: Boolean(
                  initialAssetFeatures.has_air_conditioner || 0
                ),
                target_has_pool: Boolean(initialAssetFeatures.has_pool || 0),
                target_is_exterior: Boolean(
                  initialAssetFeatures.is_exterior || 0
                ),
              });

              // Initialize comparablesData with empty structure to allow similar comparables fetch
              // This enables the similar comparables useEffect to execute even when there's no ECO data
              if (!comparablesData && initialAssetDetails) {
                setComparablesData({
                  lat: initialAssetDetails.lat,
                  lng: initialAssetDetails.lon,
                  comparables: null,
                  similarComparables: null,
                });
              }

              // Mark comparables calls as completed even when there's no data
              // This allows the report to render with available data (indicators, etc.)
              markApiCallsAsCompleted(8);
              // Note: Don't set similarComparablesFetched here - let the similar comparables useEffect handle it
            }
          }
        }
      };
      initialize();
    }
  }, [initialAssetDetails, assetValue, initialIndicators, selectedComparables]);

  // Configure report exclusions only for listing_type 8 or 12 (report style is respected
  // for the rest). It is applied only once, so the user can still re-enable the excluded
  // operation from the modules settings.
  useEffect(() => {
    if (
      reportData &&
      initialAssetDetails &&
      !autoExcludeApplied.current &&
      (initialAssetDetails.reportType === 2 ||
        initialAssetDetails.reportType === 3) &&
      (initialAssetDetails.listingType === 8 ||
        initialAssetDetails.listingType === 12)
    ) {
      // Check if we need to update the reportData to avoid infinite loops
      const needsUpdate =
        (initialAssetDetails.operation === 1 && !reportData.exclude_rent) ||
        (initialAssetDetails.operation === 0 && !reportData.exclude_sale);

      if (needsUpdate) {
        const updatedReportData = { ...reportData };
        if (initialAssetDetails.operation === 1) {
          // Sale operation - exclude rent data
          updatedReportData.exclude_rent = true;
        } else if (initialAssetDetails.operation === 0) {
          // Rent operation - exclude sale data
          updatedReportData.exclude_sale = true;
        }
        autoExcludeApplied.current = true;
        setReportData(updatedReportData);
      }
    }
  }, [reportData, initialAssetDetails]);

  // we separate the asset profile data from the initialization because the floor or other details may not be available
  // in the initialization call and we need to wait for the valuation call to have them
  useEffect(() => {
    if (
      initialAssetDetails &&
      initialAssetFeatures &&
      initialIndicators &&
      valuationData
    ) {
      setAssetProfileData(
        mountAssetProfileData(
          initialAssetDetails,
          initialAssetFeatures,
          initialIndicators,
          country
        )
      );
    }
  }, [
    initialAssetDetails,
    initialAssetFeatures,
    initialIndicators,
    valuationData,
    country,
  ]);

  // valuations rent/sale fetched -> mount data for the valuation
  useEffect(() => {
    if (
      reportData &&
      valuationIndicators &&
      initialIndicators &&
      initialAssetDetails &&
      (saleValuationData ||
        rentValuationData ||
        (initialAssetDetails.estimatedPrice &&
          initialAssetDetails.estimatedPriceSqm))
    ) {
      // Check if valuation calls failed or returned 204
      // In custom reports the value of the report operation comes from the URL params,
      // so that operation is never "failed" even when the AVM has no data
      const rentValuationFailed =
        (!rentValuationData || rentValuationData.no_data === true) &&
        !(hasCustomValuationValues && initialAssetDetails.operation === 0);
      const saleValuationFailed =
        (!saleValuationData || saleValuationData.no_data === true) &&
        !(hasCustomValuationValues && initialAssetDetails.operation === 1);

      const payload = mountValuationData(
        saleValuationData,
        rentValuationData,
        valuationIndicators,
        initialAssetDetails.operation,
        reportData.exclude_rent,
        reportData.exclude_sale,
        initialIndicators.admin5,
        initialIndicators.admin4,
        initialIndicators.admin3,
        initialAssetDetails.reportType,
        initialAssetDetails.estimatedPrice ?? null,
        initialAssetDetails.estimatedPriceSqm ?? null,
        initialAssetDetails.estimatedMin ?? null,
        initialAssetDetails.estimatedMax ?? null,
        initialAssetDetails.listingType,
        financialData?.social_financial,
        rentValuationFailed,
        saleValuationFailed,
        initialAssetDetails.yield,
        initialAssetDetails.rentValue,
        initialAssetDetails.saleValue,
        initialAssetDetails.rentValueSqm,
        initialAssetDetails.saleValueSqm,
        initialAssetDetails.area
      );
      setValuationData(payload);
    }
  }, [
    reportData,
    initialIndicators,
    valuationIndicators,
    initialAssetDetails,
    rentValuationData,
    saleValuationData,
  ]);
  // histogram indicators fetched -> mount data for the histogram
  useEffect(() => {
    if (
      histogramIndicators &&
      initialAssetDetails &&
      initialIndicators &&
      assetValueSqm &&
      assetValue
    ) {
      setHistogramData(
        mountHistogramData(
          histogramIndicators,
          initialAssetDetails.operation,
          {
            admin1: initialIndicators.admin1,
            admin2: initialIndicators.admin2,
            admin3: initialIndicators.admin3,
            admin4: initialIndicators.admin4,
            admin5: initialIndicators.admin5,
          },
          assetValueSqm,
          assetValue,
          initialAssetDetails.listingType
        )
      );
    } else {
    }
  }, [
    histogramIndicators,
    assetValueSqm,
    initialAssetDetails,
    initialIndicators,
  ]);
  // once we have the valuation features, we search for similar assets -> mount data for the scatter plot
  useEffect(() => {
    if (
      initialAssetFeatures &&
      initialAssetDetails &&
      !similarComparablesFetched &&
      comparablesData
    ) {
      const initialize = async () => {
        const area = initialAssetDetails.area;
        const areaInc = Number(area) * 1.4;
        const areaDec = Number(area) * 0.6;
        const formattedAreaInc = areaInc.toFixed(0);
        const formattedAreaDec = areaDec.toFixed(0);
        const formattedArea = [formattedAreaDec, formattedAreaInc].join(',');
        const assetProfiling = {
          area: formattedArea,
          operation_type: initialAssetDetails.operation,
          typology_type: initialAssetDetails.listingType,
          lat: Number(initialAssetDetails.lat).toFixed(4),
          lon: Number(initialAssetDetails.lon).toFixed(4),
          is_active: 0,
          is_duplicated: 0,
          distance: getDistance(initialAssetDetails.propertyType),
          limit: 50,
          date_main_range: getDatesRange(initialAssetDetails.propertyType),
        };

        const [similarComparablesResponse] = await Promise.all([
          getSimilarComparables({ ...assetProfiling }),
        ]);

        // Update comparablesData with similar comparables if available
        // Preserve ECO comparables if they exist
        if (
          similarComparablesResponse &&
          Array.isArray(similarComparablesResponse) &&
          similarComparablesResponse.length > 0
        ) {
          setComparablesData(
            (prevComparablesData: ComparablesDataModel | undefined) => {
              const updated = {
                lat: initialAssetDetails.lat,
                lng: initialAssetDetails.lon,
                comparables: prevComparablesData?.comparables ?? null,
                similarComparables: similarComparablesResponse,
              };

              // Update scatterData immediately with the new data to avoid timing issues
              if (
                initialAssetDetails &&
                (updated.comparables || updated.similarComparables)
              ) {
                setScatterData({
                  reportType: initialAssetDetails.reportType,
                  operation: initialAssetDetails.operation,
                  comparables: updated.comparables ?? null,
                  similarComparables: updated.similarComparables ?? [],
                  asset: [
                    {
                      x: Number(initialAssetDetails.area),
                      y:
                        initialAssetDetails.listingType === 7 ||
                        initialAssetDetails.listingType === 11
                          ? assetValue ?? 0
                          : assetValueSqm ?? 0,
                      z: 30,
                    },
                  ],
                });
              }

              return updated;
            }
          );
        }
        // Note: scatterData will be updated by the separate useEffect that observes comparablesData
      };
      initialize();
      setSimilarComparablesFetched(true);
    }
  }, [
    initialAssetDetails,
    initialAssetFeatures,
    valuationFeatures,
    assetValue,
    assetValueSqm,
  ]);

  // once we have the valuation , either with or without the ECO comparables list , we call to get the similar comparables and save them
  useEffect(() => {
    if (initialAssetDetails) {
      const initialize = async () => {
        let parsedComparables = null;
        if (ECOComparablesList) {
          parsedComparables = ECOComparablesList.map(
            (comparable) => comparable
          ).join(',');
        }
        if (selectedComparables) {
          parsedComparables = selectedComparables;
        }
        let payload: {
          lat: number;
          lon: number;
          id_uda?: string | String[];
          operation_type: number;
        } = {
          lat: Number(Number(initialAssetDetails.lat).toFixed(4)),
          lon: Number(Number(initialAssetDetails.lon).toFixed(4)),
          operation_type: initialAssetDetails.operation,
        };
        if (parsedComparables) {
          payload = {
            ...payload,
            id_uda: parsedComparables,
          };
          const [comparablesResponse] = await Promise.all([
            getComparablesECO(payload),
          ]);
          if (comparablesResponse) {
            let values = [];
            values = comparablesResponse.map((comparable: Asset) => {
              const isMainPrice =
                initialAssetDetails.operation == 1 ||
                initialAssetDetails.listingType == 7 ||
                initialAssetDetails.listingType == 11;
              return getCorrectPrice(
                comparable,
                isMainPrice,
                selectedPriceType === 'closing'
              );
            });
            const estimatedValueMin = Math.min(...values);
            const estimatedValueMax = Math.max(...values);
            setEstimatedValueMin(estimatedValueMin);
            setEstimatedValueMax(estimatedValueMax);
            const updatedComparablesData = mountComparablesData(
              comparablesResponse,
              initialAssetDetails.lat,
              initialAssetDetails.lon
            );
            setComparablesData(
              (prevComparablesData: ComparablesDataModel | undefined) => {
                const updated = {
                  ...updatedComparablesData,
                  similarComparables:
                    prevComparablesData?.similarComparables ?? null,
                };

                // Update scatterData immediately with the new data to avoid timing issues
                if (
                  initialAssetDetails &&
                  (updated.comparables || updated.similarComparables)
                ) {
                  setScatterData({
                    reportType: initialAssetDetails.reportType,
                    operation: initialAssetDetails.operation,
                    comparables: updated.comparables ?? null,
                    similarComparables: updated.similarComparables ?? [],
                    asset: [
                      {
                        x: Number(initialAssetDetails.area),
                        y:
                          initialAssetDetails.listingType === 7 ||
                          initialAssetDetails.listingType === 11
                            ? assetValue ?? 0
                            : assetValueSqm ?? 0,
                        z: 30,
                      },
                    ],
                  });
                }

                return updated;
              }
            );
          }
        }
      };
      initialize();
    }
  }, [
    ECOComparablesList,
    selectedComparables,
    selectedPriceType,
    initialAssetDetails,
  ]);

  // Update scatterData when assetValue or assetValueSqm changes (to update the asset point position)
  // Note: scatterData is already updated directly when comparablesData changes to avoid timing issues
  useEffect(() => {
    if (
      scatterData &&
      initialAssetDetails &&
      (assetValue !== null || assetValueSqm !== null)
    ) {
      // Only update the asset point position, preserve comparables
      setScatterData((prevScatterData: ScatterDataModel | undefined) => ({
        ...prevScatterData!,
        asset: [
          {
            x: Number(initialAssetDetails.area),
            y:
              initialAssetDetails.listingType === 7 ||
              initialAssetDetails.listingType === 11
                ? assetValue ?? 0
                : assetValueSqm ?? 0,
            z: 30,
          },
        ],
      }));
    }
  }, [assetValue, assetValueSqm, initialAssetDetails]);

  // set the estimated min and max in the initial asset details
  useEffect(() => {
    if (initialAssetDetails && estimatedValueMin && estimatedValueMax) {
      setInitialAssetDetails({
        ...initialAssetDetails,
        estimatedMin: estimatedValueMin,
        estimatedMax: estimatedValueMax,
      });
    }
  }, [estimatedValueMin, estimatedValueMax]);

  // parallel indicators calls that do not depend on more than the initialization
  useEffect(() => {
    if (initialIndicators && initialAssetDetails) {
      const initialize = async () => {
        const boundary_id_district = `${initialIndicators.admin3},${initialIndicators.admin4},${initialIndicators.admin5}`;
        const boundary_id_country = `${initialIndicators.admin0},${initialIndicators.admin3},${initialIndicators.admin5}`;
        const period_code = getPeriodCodes();
        const params = {
          boundary_id: boundary_id_district,
          // period_code: "2024Q1",
          typology_type: initialAssetDetails.listingType,
          operation: initialAssetDetails.operation,
        };
        const [
          valuationIndicatorsResponse,
          histogramIndicatorsResponse,
          financialIndicatorsResponse,
          offerDemandIndicatorsResponse,
          zonePopulationIndicatorsResponse,
          zoneIndicatorsResponse,
        ] = await Promise.all([
          getValuationIndicators({
            boundary_id: boundary_id_district,
            typology_type: initialAssetDetails.listingType,
          }),
          getHistogramIndicators(params),
          getFinancialIndicators({
            ...params,
            typology_type:
              initialAssetDetails.listingType === 1
                ? 1
                : [initialAssetDetails.listingType, 1],
            boundary_id: boundary_id_country,
          }),
          getOfferDemandIndicators({
            typology_type: initialAssetDetails.listingType,
            boundary_id: initialIndicators.admin5,
          }),
          getZonePopulationIndicators({
            boundary_id: initialIndicators.admin5,
            operation: initialAssetDetails.operation,
            typology_type: initialAssetDetails.listingType,
          }),
          getZoneIndicators({
            lat: initialAssetDetails.lat,
            lon: initialAssetDetails.lon,
          }),
        ]);

        setValuationIndicators(valuationIndicatorsResponse);
        setHistogramIndicators(histogramIndicatorsResponse);
        setFinancialData(
          mountFinancialData(
            financialIndicatorsResponse,
            initialAssetDetails.operation,
            initialAssetDetails.listingType
          )
        );
        setOfferDemandData(
          mountOfferDemandData(
            offerDemandIndicatorsResponse,
            initialAssetDetails.operation
          )
        );
        if (zonePopulationIndicatorsResponse) {
          setZonePopulationData(
            mountZonePopulationData(zonePopulationIndicatorsResponse[0])
          );
        }
        setZoneData(
          mountZoneData(
            initialAssetDetails.lat,
            initialAssetDetails.lon,
            zoneIndicatorsResponse
          )
        );
      };
      initialize();
    }
  }, [initialIndicators]);

  const rentValuationCall = async (): Promise<ValuationResponse | null> => {
    if (initialAssetDetails) {
      // Required params that must be present
      const requiredParams = {
        typology_type: initialAssetDetails.listingType ?? 0,
        operation: 0,
        lat: initialAssetDetails.lat ?? 0,
        lon: initialAssetDetails.lon ?? 0,
        area: initialAssetDetails.area ?? 0,
        address: initialAssetDetails.address ?? '',
      };

      // Optional params that will be added only if they have values
      const optionalParams = {
        ...initialAssetFeatures,
        floor: initialAssetDetails.floor,
        build_status: initialAssetDetails.buildStatus,
        property_type: initialAssetDetails.propertyType,
        year_build: initialAssetDetails.yearBuilt,
        nbaths: initialAssetDetails.nbaths,
        rooms: initialAssetDetails.rooms,
      };

      // Filter out undefined and null values from optional params
      const filteredOptionalParams = Object.fromEntries(
        Object.entries(optionalParams).filter(([_, value]) => value != null)
      );

      const params: ValuationRequestParams = {
        ...requiredParams,
        ...filteredOptionalParams,
      };

      // if (
      //   initialAssetDetails.nbaths !== null &&
      //   initialAssetDetails.nbaths !== undefined
      // ) {
      //   params.nbaths = initialAssetDetails.nbaths;
      // }
      // if (
      //   initialAssetDetails.rooms !== null &&
      //   initialAssetDetails.rooms !== undefined
      // ) {
      //   params.rooms = initialAssetDetails.rooms;
      // }

      try {
        const valuationResponse = await getValuationData(params);
        return valuationResponse;
      } catch (error) {
        console.error('❌ RentValuationCall - Error:', error);
        const errorLog = getRequestErrorLog(
          error,
          `${window.location.href}`,
          params
        );
        addError(errorLog);
        return null;
      }
    }
    return null;
  };

  const saleValuationCall = async (): Promise<ValuationResponse | null> => {
    if (initialAssetDetails) {
      // Required params that must be present
      const requiredParams = {
        typology_type: initialAssetDetails.listingType ?? 0,
        operation: 1,
        lat: initialAssetDetails.lat ?? 0,
        lon: initialAssetDetails.lon ?? 0,
        area: initialAssetDetails.area ?? 0,
        address: initialAssetDetails.address ?? '',
      };

      // Optional params that will be added only if they have values
      const optionalParams = {
        ...initialAssetFeatures,
        floor: initialAssetDetails.floor,
        build_status: initialAssetDetails.buildStatus,
        property_type: initialAssetDetails.propertyType,
        year_build: initialAssetDetails.yearBuilt,
        nbaths: initialAssetDetails.nbaths,
        rooms: initialAssetDetails.rooms,
      };

      // Filter out undefined and null values from optional params
      const filteredOptionalParams = Object.fromEntries(
        Object.entries(optionalParams).filter(([_, value]) => value != null)
      );

      const params: ValuationRequestParams = {
        ...requiredParams,
        ...filteredOptionalParams,
      };

      try {
        const valuationResponse = await getValuationData(params);
        return valuationResponse;
      } catch (error) {
        console.error('❌ SaleValuationCall - Error:', error);
        const errorLog = getRequestErrorLog(
          error,
          `${window.location.href}`,
          params
        );
        addError(errorLog);
        return null;
      }
    }
    return null;
  };

  const initIndicatorsCall = async () => {
    const indicatorsResponse = await getInitialIndicators({
      operation: searchParams.get('operation'),
      lat: searchParams.get('lat'),
      lon: searchParams.get('lon'),
    });
    return indicatorsResponse;
  };

  const handlePDFGenerationWithState = useCallback(async () => {
    setIsPrinting(true);
    try {
      await handlePDFGeneration();
    } finally {
      setIsPrinting(false);
    }
  }, []);

  // Función para manejar el toggle de módulos
  const handleModuleToggle = useCallback(
    (moduleId: string, visible: boolean) => {
      setVisibleModules((prev) => ({
        ...prev,
        [moduleId]: visible,
      }));
    },
    []
  );

  // Función para manejar el cambio de exclude_rent y exclude_sale
  const handleExcludeChange = useCallback(
    (excludeRent: boolean, excludeSale: boolean) => {
      if (reportData) {
        setReportData({
          ...reportData,
          exclude_rent: excludeRent,
          exclude_sale: excludeSale,
        });
      }
    },
    [reportData]
  );

  // send error report at the end of the generation process if there are any errors
  useEffect(() => {
    // A failing AVM does not block a custom report: it is still reported, but the
    // report is rendered with the estimated values coming from the URL params
    const blockingErrors = hasCustomValuationValues
      ? errorReport.errors.filter(
          (error) => !error.endpoint?.includes('/api/valuation/v2/valuate')
        )
      : errorReport.errors;

    if (blockingErrors.length > 0 && !errorReportSent) {
      setErrorsFound(true);
      sendErrorReport(errorReport);
      setErrorReportSent(true);
    } else if (
      /* aqui readyToSendErrorReport da false con report type 3 */
      readyToSendErrorReport &&
      !errorReportSent &&
      blockingErrors.length === 0 &&
      !errorsFound
    ) {
      if (errorReport.errors.length > 0) {
        sendErrorReport(errorReport);
        setErrorReportSent(true);
      }
      setRenderReport(true);
    }
  }, [
    errorReport,
    readyToSendErrorReport,
    errorReportSent,
    hasCustomValuationValues,
  ]);

  // for debugging
  useEffect(() => {
    if (readyToSendErrorReport && !errorReportSent) {
    }
  }, [readyToSendErrorReport, errorReportSent, renderReport]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data && event.data.type === 'TRIGGER_DOWNLOAD') {
        // Notify parent that the child received the trigger
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'TRIGGER_DOWNLOAD_RECEIVED' }, '*');
          handlePDFGenerationWithState();
        }
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handlePDFGenerationWithState]);

  // Add new useEffect to notify parent when report is ready
  useEffect(() => {
    if (renderReport && similarComparablesCall && window.parent !== window) {
      window.parent.postMessage({ type: 'REPORT_READY' }, '*');
    }
  }, [renderReport, similarComparablesCall]);

  return (
    <ErrorBoundary>
      <I18nProvider locale={selectedLanguage} country={country}>
        {renderReport && initialAssetDetails && reportData ? (
          <div className="app-gp">
            {typeof window !== 'undefined' && window.self === window.top && (
              <DownloadButton />
            )}
            <ModuleToggle
              onModuleToggle={handleModuleToggle}
              visibleModules={visibleModules}
              isReportRendered={renderReport}
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
              onPriceTypeChange={setSelectedPriceType}
              excludeRent={reportData.exclude_rent}
              excludeSale={reportData.exclude_sale}
              onExcludeChange={handleExcludeChange}
              operation={initialAssetDetails.operation}
              reportType={initialAssetDetails.reportType}
            />
            <GlobalStyle />
            {visibleModules.cover && (
              <PreviewPage id="module-cover" data-module-id="cover">
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  <ReportCover
                    {...{
                      reportData,
                      listingType: initialAssetDetails.listingType,
                      operation: initialAssetDetails.operation,
                      address: initialAssetDetails.address ?? '',
                    }}
                  />
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
            {visibleModules.agentInfo && (
              <PreviewPage id="module-agentInfo" data-module-id="agentInfo">
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  <AgentInfo {...reportData} />
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
            {(visibleModules.assetProfile || visibleModules.valuation) && (
              <PreviewPage
                id="module-assetProfile"
                data-module-id="assetProfile"
              >
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  {visibleModules.assetProfile && (
                    <div id="module-assetProfile" data-module-id="assetProfile">
                      <AssetProfile
                        {...(assetProfileData as AssetProfileDataModel)}
                        reportType={initialAssetDetails.reportType}
                      />
                    </div>
                  )}
                  {visibleModules.valuation && (
                    <div id="module-valuation" data-module-id="valuation">
                      <Valuation
                        {...(valuationData as ValuationDataModel)}
                        country={country}
                      />
                    </div>
                  )}
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}

            {/* Conditional structure based on listingType - only show if NOT listing_type=8/12 AND report_type=2 */}
            {!(
              ((initialAssetDetails.listingType === 8 ||
                initialAssetDetails.listingType === 12) &&
                initialAssetDetails.reportType === 2) ||
              initialAssetDetails.reportType === 3
            ) && (
              <>
                {/* Original structure for all listing types except 8/12 with report_type=2 */}
                {(visibleModules.marketValueGraph ||
                  visibleModules.comparativeGraph) && (
                  <>
                    {(visibleModules.marketValueGraph ||
                      visibleModules.comparativeGraph) && (
                      <PreviewPage
                        id="module-marketValueGraph"
                        data-module-id="marketValueGraph"
                      >
                        <PageModulesContainer>
                          <AssetReportHeader
                            data={reportData}
                            listingType={initialAssetDetails.listingType}
                            reportType={initialAssetDetails.reportType}
                            operation={initialAssetDetails.operation}
                          />
                          {/* Primer módulo - MarketValueGraph si está habilitado, sino ComparativeGraph si está habilitado */}
                          {visibleModules.marketValueGraph &&
                          histogramData &&
                          histogramData.averagePrice &&
                          histogramData.buckets &&
                          histogramData.buckets.filter(
                            (bucket) =>
                              bucket.bucket !== 0 &&
                              bucket.value !== null &&
                              bucket.units !== null
                          ).length >= 2 ? (
                            <div
                              id="module-marketValueGraph"
                              data-module-id="marketValueGraph"
                            >
                              <AssetValueOnMarket
                                {...histogramData}
                                country={country}
                              />
                            </div>
                          ) : visibleModules.marketValueGraph ? (
                            <NoDataHalfPage
                              operation={initialAssetDetails.operation}
                              title="marketGraph.subtitle"
                              subtitleSale="marketGraph.subtitleOperationSale"
                              subtitleRent="marketGraph.subtitleOperationRent"
                              showLoading={false}
                            />
                          ) : visibleModules.comparativeGraph && scatterData ? (
                            <div
                              id="module-comparativeGraph"
                              data-module-id="comparativeGraph"
                            >
                              <AssetComparativeGraph
                                {...scatterData}
                                country={country}
                                useCorrectedPrice={
                                  selectedPriceType === 'closing'
                                }
                              />
                            </div>
                          ) : visibleModules.comparativeGraph ? (
                            <NoDataHalfPage
                              operation={initialAssetDetails.operation}
                              title="marketGraph.subtitle"
                              subtitleSale="marketGraph.subtitleOperationSale"
                              subtitleRent="marketGraph.subtitleOperationRent"
                              loaded={
                                similarComparablesCall &&
                                similarComparablesFetched
                              }
                              showLoading={true}
                            />
                          ) : null}

                          {/* Segundo módulo - solo ComparativeGraph si MarketValueGraph también está habilitado */}
                          {visibleModules.marketValueGraph &&
                          visibleModules.comparativeGraph &&
                          scatterData ? (
                            <div
                              id="module-comparativeGraph"
                              data-module-id="comparativeGraph"
                            >
                              <AssetComparativeGraph
                                {...scatterData}
                                country={country}
                                useCorrectedPrice={
                                  selectedPriceType === 'closing'
                                }
                              />
                            </div>
                          ) : visibleModules.marketValueGraph &&
                            visibleModules.comparativeGraph ? (
                            <NoDataHalfPage
                              operation={initialAssetDetails.operation}
                              title="marketGraph.subtitle"
                              subtitleSale="marketGraph.subtitleOperationSale"
                              subtitleRent="marketGraph.subtitleOperationRent"
                              loaded={
                                similarComparablesCall &&
                                similarComparablesFetched
                              }
                              showLoading={true}
                            />
                          ) : (
                            <div style={{ minHeight: '400px' }} />
                          )}
                          <AssetReportFooter {...reportData} />
                        </PageModulesContainer>
                      </PreviewPage>
                    )}
                  </>
                )}
                {(visibleModules.financialData ||
                  visibleModules.offerDemand) && (
                  <PreviewPage
                    id="module-financialData"
                    data-module-id="financialData"
                  >
                    <PageModulesContainer>
                      <AssetReportHeader
                        data={reportData}
                        listingType={initialAssetDetails.listingType}
                        reportType={initialAssetDetails.reportType}
                        operation={initialAssetDetails.operation}
                      />
                      {/* Primer módulo - FinancialData si está habilitado, sino OfferDemand si está habilitado */}
                      {visibleModules.financialData ? (
                        <div
                          id="module-financialData"
                          data-module-id="financialData"
                        >
                          <FinancialData
                            {...(financialData || {
                              social_financial: null,
                              evolution: null,
                              operation: initialAssetDetails.operation,
                            })}
                            country={country}
                          />
                        </div>
                      ) : visibleModules.offerDemand && offerDemandData ? (
                        <div
                          id="module-offerDemand"
                          data-module-id="offerDemand"
                        >
                          <OfferDemand {...offerDemandData} />
                        </div>
                      ) : visibleModules.offerDemand ? (
                        <NoDataHalfPage
                          operation={initialAssetDetails.operation}
                          title="Oferta y demanda"
                          subtitleSale="Oferta"
                          subtitleRent="Demanda"
                        />
                      ) : null}

                      {/* Segundo módulo - solo OfferDemand si FinancialData también está habilitado */}
                      {visibleModules.financialData &&
                      visibleModules.offerDemand &&
                      offerDemandData ? (
                        <div
                          id="module-offerDemand"
                          data-module-id="offerDemand"
                        >
                          <OfferDemand {...offerDemandData} />
                        </div>
                      ) : visibleModules.financialData &&
                        visibleModules.offerDemand ? (
                        <NoDataHalfPage
                          operation={initialAssetDetails.operation}
                          title="Oferta y demanda"
                          subtitleSale="Oferta"
                          subtitleRent="Demanda"
                        />
                      ) : (
                        <div style={{ minHeight: '400px' }} />
                      )}
                      <AssetReportFooter {...reportData} />
                    </PageModulesContainer>
                  </PreviewPage>
                )}
              </>
            )}
            {(visibleModules.zonePopulation || visibleModules.zone) && (
              <PreviewPage
                id="module-zonePopulation"
                data-module-id="zonePopulation"
              >
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  {/* Primer módulo - ZonePopulation si está habilitado, sino Zone si está habilitado */}
                  {visibleModules.zonePopulation && zonePopulationData ? (
                    <div
                      id="module-zonePopulation"
                      data-module-id="zonePopulation"
                    >
                      <ZonePopulation {...zonePopulationData} />
                    </div>
                  ) : visibleModules.zonePopulation ? (
                    <NoDataHalfPage
                      operation={initialAssetDetails.operation}
                      title="zonePopulation.title"
                      subtitleSale="marketGraph.subtitleOperationSale"
                      subtitleRent="marketGraph.subtitleOperationRent"
                    />
                  ) : visibleModules.zone && zoneData ? (
                    <div id="module-zone" data-module-id="zone">
                      <Zone {...zoneData} />
                    </div>
                  ) : visibleModules.zone ? (
                    <NoDataHalfPage
                      operation={initialAssetDetails.operation}
                      title="marketGraph.subtitle"
                      subtitleSale="marketGraph.subtitleOperationSale"
                      subtitleRent="marketGraph.subtitleOperationRent"
                    />
                  ) : null}

                  {/* Segundo módulo - solo Zone si ZonePopulation también está habilitado */}
                  {visibleModules.zonePopulation &&
                  visibleModules.zone &&
                  zoneData ? (
                    <div id="module-zone" data-module-id="zone">
                      <Zone {...zoneData} />
                    </div>
                  ) : visibleModules.zonePopulation && visibleModules.zone ? (
                    <NoDataHalfPage
                      operation={initialAssetDetails.operation}
                      title="marketGraph.subtitle"
                      subtitleSale="marketGraph.subtitleOperationSale"
                      subtitleRent="marketGraph.subtitleOperationRent"
                    />
                  ) : (
                    <div style={{ minHeight: '400px' }} />
                  )}
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
            {visibleModules.comparables &&
              comparablesData &&
              comparablesData.comparables &&
              initialAssetDetails.reportType !== 3 && (
                <div id="module-comparables" data-module-id="comparables">
                  <Comparables
                    comparablesData={comparablesData}
                    reportData={reportData}
                    listingType={initialAssetDetails.listingType}
                    operation={initialAssetDetails.operation}
                    country={country}
                    useCorrectedPrice={selectedPriceType === 'closing'}
                  />
                </div>
              )}
            {visibleModules.glossary && (
              <PreviewPage id="module-glossary" data-module-id="glossary">
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  <Glossary {...glossaryTexts} />
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
            {visibleModules.terms && (
              <PreviewPage id="module-terms" data-module-id="terms">
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  <Terms {...termsTexts} />
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
            {visibleModules.backCover && (
              <PreviewPage id="module-backCover" data-module-id="backCover">
                <PageModulesContainer>
                  <AssetReportHeader
                    data={reportData}
                    listingType={initialAssetDetails.listingType}
                    reportType={initialAssetDetails.reportType}
                    operation={initialAssetDetails.operation}
                  />
                  <ReportBackCover {...reportData} />
                  <AssetReportFooter {...reportData} />
                </PageModulesContainer>
              </PreviewPage>
            )}
          </div>
        ) : (
          <>
            {!errorsFound ? (
              <LoadingComponent />
            ) : (
              <ErrorComponent
                customMessageId={
                  valuationFailed ? 'error.valuationFailed' : undefined
                }
              />
            )}
          </>
        )}
      </I18nProvider>
    </ErrorBoundary>
  );
}
